import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import {
  ConversionOfSharesForm,
  ConvertSharesPerShareholderModel,
} from "./conversionOfSharesDomain";
import { VBox } from "@/components-v2/Box";
import { MSelect } from "@/components-v2/Select";
import { ArticleSelectorQuestion, YesNoQuestion } from "@/dbot/common/commonComponents";
import { useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { MTextInput } from "@/components-v2/TextInput";
import { LinkButton } from "@/components-v2/Button";
import { ChangeArticlesOfAssociation } from "@/dbot/changeArticlesOfAssociation/changeArticlesOfAssociationComponents";
import { UpdateArticlesAutomatically } from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalComponents";
import { findShareTypeName } from "@/common/share";
import { NewShareClassDialog } from "@/shareholderRegister/transactions/commonTransactionComponents";

export const ConversionOfSharesView: FC<{ form: ConversionOfSharesForm }> = observer(({ form }) => {
  const { t, lang } = useTranslation();
  const { http } = useRootStore();
  useEffect(() => {
    return form.init();
  }, [form]);

  return (
    <VBox gap={"md"}>
      <MSelect
        model={form.fromShareTypeModel}
        label={t("decisions.conversionOfShares.fromShareClass")}
        disabled={form.isProjectLocked}
        labelFn={(shareType) => findShareTypeName(shareType, lang)}
        fullWidth
      />
      <VBox alignItems={"start"}>
        <MSelect
          model={form.toShareTypeModel}
          label={t("decisions.conversionOfShares.toShareClass")}
          disabled={form.isProjectLocked}
          labelFn={(shareType) => (shareType ? findShareTypeName(shareType, lang) : " ")}
          fullWidth
        />

        {form.newShareClassController && (
          <NewShareClassDialog controller={form.newShareClassController} />
        )}
        <LinkButton
          label={t("decisions.conversionOfShares.createShareClass")}
          icon={"plus"}
          onClick={() => form.openNewShareClassDialog(http, lang)}
          disabled={form.isProjectLocked}
        />
      </VBox>
      <UpdateArticlesAutomatically
        updateArticlesAutomatically={form.updateArticlesAutomatically}
        isProjectLocked={form.isProjectLocked}
      />
      {form.isUpdateArticlesAutomatically && (
        <ArticleSelectorQuestion
          question={t("decisions.conversionOfShares.articleOfAssociationNumber")}
          model={form.articleOfAssociationNumber}
          isLocked={form.isProjectLocked}
          onChange={form.articleOfAssociationNumber.setSelected}
        />
      )}
      {form.isUpdateArticlesManually && (
        <ChangeArticlesOfAssociation model={form.articleChangeModel} />
      )}
      <YesNoQuestion
        question={t("decisions.conversionOfShares.convertAllShares")}
        field={form.convertAllShares}
      />
      {!form.convertAllShares.value && (
        <VBox gap={"md"}>
          <strong>{t("decisions.conversionOfShares.sharesPerShareholder")}</strong>
          <SharesPerShareholderTable form={form} />
        </VBox>
      )}
    </VBox>
  );
});

const SharesPerShareholderTable: FC<{ form: ConversionOfSharesForm }> = observer(
  function SharesPerShareholderTable({ form }) {
    const { t, lang } = useTranslation();
    const columns: TableColumn<ConvertSharesPerShareholderModel>[] = [
      {
        name: "shareholder.name",
        label: t("decisions.conversionOfShares.shareholder"),
        align: "left",
        width: "50%",
        getValue: (p) => <div>{p.shareholder.name}</div>,
      },
      {
        name: "numberOfShares",
        label: findShareTypeName(form.fromShareTypeModel.value, lang),
        align: "right",
        getValue: (p) => (
          <div>{p.currentNumberOfSharesForShareType(form.fromShareTypeModel.value)}</div>
        ),
      },
      {
        name: "numberOfSharesToConvert",
        label: t("decisions.conversionOfShares.numberOfShares"),
        align: "left",
        notSorted: true,
        getValue: (shareholderModel) => (
          <MTextInput
            name={`shareholder.${shareholderModel.shareholder.name}.number`}
            model={shareholderModel.numberOfShares}
            onBlur={() => {
              shareholderModel.numberOfShares.commit();
              shareholderModel.validate(form.fromShareTypeModel.value);
            }}
            disabled={form.isProjectLocked}
          />
        ),
      },
    ];
    return (
      <DataTable
        variant={"EDITABLE"}
        tableColumns={columns}
        tableData={form.sharesPerShareholder}
        getId={(row) => row.id}
      />
    );
  },
);
